import Phaser from 'phaser';
import type { LoadedActor } from '../anim/actor-clipset';
import { ControlMap, KeyboardSource, GamepadSource, TouchSource } from '../control/ControlMap';
import { Actor } from '../actor/Actor';
import { resolveCombat } from '../combat/overlap';
import { Hud } from '../hud/Hud';
import { TEX_RIPPLE, TEX_BACKDROP, ACTOR_REGISTRY_KEY } from './BootScene';
import { applyLightSkin, type AppliedSkin } from '../light/LightSkin';
import { PuddleReflection } from '../light/PuddleReflection';
import { reflectionAt, type ReflectiveZone } from '../light/reflective-ground';
import { shadeAt } from '../light/shade-ground';
import { configureGround, configurePerspective, configureWalkable } from '../game/ground';
import { sunShear, type GroundSun } from '@gooddesign/anim-schema/ground-math';
import { VIEW_WIDTH } from '../game/Game';
import type { SceneDef, SceneExit, SceneSpawn, SceneShadeZone } from '../scene/scene-format';
import { parseColor } from '../scene/scene-format';
import {
  SCENE_REGISTRY_KEY,
  effectiveSkin,
  placeProp,
  puddleScreenCentre,
  reflectiveZones,
  requestScene,
} from '../scene/scene-registry';
import { hasFlag, setFlag } from '../game/story-state';
import { EditRunner } from './EditRunner';
import { Npc } from '../actor/Npc';
import { loadedNpcRig } from '../anim/npc-clipset';

// SceneRunner — the engine that LOADS a location. Where AlleyScene once hardcoded
// the alley across its own body, this reads one SceneDef (src/scene/scene-format)
// and builds the same location from data: it seats the ground, stretches the
// plate, places the props (including the marquee that is also the key light),
// dresses the frame with the referenced light skin, wets the ground, and spawns
// the actors at their authored (x, z) depth. A second location is a second JSON
// file; not one line of this changes.
//
// The camera follows ALONG the street only. It never tracks depth, because an
// actor walking upstage is meant to recede within a fixed frame — if the camera
// chased him, the street would stop reading as a street.

// Toggle to see the ground footprints while tuning.
const DEBUG_BODIES = false;

/** Camera ease toward the player, per frame. Engine feel, not location data. */
const CAMERA_LERP = 0.12;

/** Actor footprints along the street, world px. A property of the character, not
 *  the location, so it stays here rather than in the Scene record. */
const BODY_WIDTH = { player: 20, enemy: 22 } as const;

/** Depth of the puddle-reflection group (under the actors). Engine apparatus. */
const PUDDLE_DEPTH = 3;

/** How close (ground px) the player must stand to an exit to take it, when the
 *  record doesn't say. Engine feel, not location data. */
const EXIT_RADIUS = 20;

export class SceneRunner extends Phaser.Scene {
  private def!: SceneDef;
  private control!: ControlMap;
  private touch!: TouchSource;
  private player!: Actor;
  private actors: Actor[] = [];
  private npcs: Npc[] = [];
  private hud!: Hud;
  private skin!: AppliedSkin;
  private puddles: PuddleReflection[] = [];
  private reflective: ReflectiveZone[] = [];
  /** A fixed cinema shot holds its framing; the belt follows the player. */
  private fixedCamera = false;
  /** The shot's sun, resolved to a screen rake (dx, dy) per unit of the figure's
   *  height, plus the cast-shadow opacity and edge softness (screen px).
   *  Absent → no cast shadow. */
  private sun?: { dx: number; dy: number; strength: number; softness: number };
  /** Ground regions the plate already has in shadow (darken a figure standing
   *  there, and kill its cast shadow). */
  private shade: SceneShadeZone[] = [];
  /** Latched the frame an exit fires, so the transition starts exactly once. */
  private leaving = false;
  /** The scene's edit — its beat timeline. Null for a scene with no `beats`
   *  (one implicit played beat, no advance: every scene before 16.6). */
  private edit: EditRunner | null = null;

  constructor() {
    super('Play');
  }

  // ── Authoring windows (Arcade Authoring shell, ?edit=1) ───────────────────
  // Read-only views the edit bridge overlays the stage with, plus the one live
  // handle it drags by (Npc.place). The runtime never reads these.

  /** The standing cast, for hit-testing and live drag. */
  get cast(): readonly Npc[] {
    return this.npcs;
  }

  /** The current beat's id, for the beat timeline's live indicator. */
  get currentBeatId(): string | null {
    return this.edit?.beatId ?? null;
  }

  /** Camera scroll along the street, for screen↔world conversion. */
  get cameraScrollX(): number {
    return this.cameras.main.scrollX;
  }

  create(): void {
    const def = this.registry.get(SCENE_REGISTRY_KEY) as SceneDef | undefined;
    if (!def) throw new Error('SceneRunner: no SceneDef in the registry — PreloadScene did not load one');
    this.def = def;

    // Phaser reuses this instance across a location transition (scene.start
    // destroys the game objects, not the class), so per-run state resets here,
    // not in field initializers.
    this.leaving = false;
    this.actors = [];
    this.npcs = [];
    this.puddles = [];

    // ── Ground FIRST: every projection below reads the active plane ───────────
    // The plate supplies the seat (nearY/depthPx); the ground supplies the
    // plane. A perspective shot hands its calibrated record straight through
    // (ground.ts delegates the maths to anim-schema); a belt assembles the
    // oblique numbers exactly as it always did.
    if (def.perspective) {
      configurePerspective({
        record: def.perspective,
        nearY: def.plate.nearY,
        depthPx: def.plate.depthPx,
        xMin: def.ground.xMin,
        xMax: def.ground.xMax,
      });
    } else {
      configureGround({
        depthAngleDeg: def.ground.depthAngleDeg!,
        nearY: def.plate.nearY,
        depthPx: def.plate.depthPx,
        xMin: def.ground.xMin,
        xMax: def.ground.xMax,
        nearScale: def.ground.nearScale!,
        farScale: def.ground.farScale!,
      });
    }
    // The walkable polygon rides (and clears) with the location — a stale one
    // from the previous scene would strand the player.
    configureWalkable(def.walkable ? { outer: def.walkable.points, holes: def.walkable.holes ?? [] } : null);

    // Resolve the shot's sun to a screen rake for cast shadows, and its shade
    // zones. A perspective scene's sun lives on its calibration record; either
    // way the shear itself — cot(elevation) along the azimuth — is ground-math's.
    const sun: GroundSun | undefined = def.perspective?.sun ?? def.sun;
    if (sun) {
      const shear = sunShear(sun);
      this.sun = { dx: shear.x, dy: shear.y, strength: sun.strength, softness: sun.softness };
    }
    this.shade = def.shade ?? [];

    const { widthPx, heightPx } = def.world;
    this.fixedCamera = def.camera === 'fixed';
    this.cameras.main.setBackgroundColor(def.world.bgColor);
    this.cameras.main.setBounds(0, 0, widthPx, heightPx);

    // ── The painted plate ─────────────────────────────────────────────────────
    // A belt plate is stretched over the whole scrolling level. A fixed cinema
    // shot instead fits the plate to the view WIDTH and centres it — the plate's
    // own aspect is kept and the frame letterboxes against the world bg, because
    // the composition is the shot and must not be distorted.
    const tint = parseColor(def.plate.tint);
    if (this.fixedCamera) {
      const src = this.textures.get(TEX_BACKDROP).getSourceImage();
      const aspect = src.width / src.height;
      const dw = widthPx;
      const dh = dw / aspect;
      this.add.image(0, (heightPx - dh) / 2, TEX_BACKDROP)
        .setOrigin(0, 0).setDisplaySize(dw, dh).setDepth(-10).setTint(tint);
    } else {
      this.add.image(0, 0, TEX_BACKDROP)
        .setOrigin(0, 0).setDisplaySize(widthPx, heightPx).setDepth(-10).setTint(tint);
    }

    // ── ControlMap: keyboard + gamepad + touch ───────────────────────────────
    this.touch = new TouchSource();
    this.control = new ControlMap([
      new KeyboardSource(this.input.keyboard!),
      new GamepadSource(),
      this.touch,
    ]);

    // ── Props (drawn), and the prop-id → texture map the puddles reflect by ────
    const propTexture = new Map<string, string>();
    for (const prop of def.props) {
      placeProp(this, prop);
      propTexture.set(prop.id, prop.texture);
    }

    // ── Actors, spawned at their authored (x, z) ─────────────────────────────
    const actor = this.registry.get(ACTOR_REGISTRY_KEY) as LoadedActor | undefined;
    if (!actor) throw new Error('SceneRunner: no actor in the registry — BootScene did not finish loading it');
    for (const spawn of def.spawns) this.spawnActor(spawn, actor);

    // ── The cast: standing NPCs, seated where the record says ────────────────
    for (const n of def.npcs ?? []) {
      const rig = loadedNpcRig(this, n.rig);
      this.npcs.push(new Npc(n.id, {
        scene: this,
        clips: rig.clips,
        x: n.x,
        z: n.z,
        rigScale: rig.scale,
        ...(n.facing ? { facing: n.facing } : {}),
        ...(n.attention
          ? { attention: { target: n.attention.target, engagement: n.attention.engagement ?? 'locked' } }
          : {}),
      }));
    }

    this.cameras.main.setScroll(this.fixedCamera ? 0 : this.cameraTargetX(), 0);

    // ── Lighting: referenced skin + the props' own lights ────────────────────
    this.skin = applyLightSkin(this, effectiveSkin(def));

    // ── Water: reflective zones + the visible puddle-reflection shots ────────
    this.reflective = reflectiveZones(def.water);
    for (const pd of def.water.puddles) {
      const src = propTexture.get(pd.reflects);
      if (!src) throw new Error(`scene '${def.id}': puddle reflects prop '${pd.reflects}', which is not a prop in this scene`);
      const c = puddleScreenCentre(pd.x, pd.z);
      this.puddles.push(new PuddleReflection({
        scene: this,
        sourceTexture: src,
        rippleTexture: TEX_RIPPLE,
        cx: c.x, cy: c.y,
        width: pd.width, height: pd.screenHeight,
        depth: PUDDLE_DEPTH,
      }));
    }

    this.hud = new Hud(this, this.touch);

    // ── The edit: the scene's beat timeline ──────────────────────────────────
    this.edit?.destroy();
    this.edit = def.beats ? new EditRunner(this, def.id, def.beats) : null;

    // The ?debug=ground overlay retired here (Order 3.9984): the Shot Editor's
    // stage overlay (?edit=1) now draws the walkable polygon (holes included),
    // the shade zones and every trigger — and can author them, which the debug
    // tracing never could.
  }

  private spawnActor(spawn: SceneSpawn, actor: LoadedActor): void {
    const isPlayer = spawn.faction === 'player';
    const a = new Actor({
      scene: this, clips: actor.clips,
      x: spawn.x, z: spawn.z,
      faction: spawn.faction,
      bodyWidth: BODY_WIDTH[spawn.faction],
      rigScale: actor.scale,
      ...(isPlayer ? { control: this.control } : {}),
      debugBody: DEBUG_BODIES,
    });
    this.actors.push(a);
    if (isPlayer) this.player = a;
  }

  /** Where the camera wants to be: the player centred along the street, clamped
   *  to the level. Depth is deliberately ignored. */
  private cameraTargetX(): number {
    const target = this.player.screen.x - VIEW_WIDTH / 2;
    return Phaser.Math.Clamp(target, 0, this.def.world.widthPx - VIEW_WIDTH);
  }

  /** The player has stepped into an armed exit: record the choice, hand the
   *  next location's id to the registry, and restart the load chain. The cut is
   *  hard — film grammar's default — and the flags survive it (story-state is
   *  module-level, deliberately outside the Phaser scene lifecycle). */
  private takeExit(exit: SceneExit): void {
    this.leaving = true;
    for (const f of exit.set ?? []) setFlag(f);
    requestScene(exit.to);
    this.sound.stopAll();
    this.scene.start('Preload');
  }

  /** An exit fires when its guards all hold and the player stands inside its
   *  trigger circle — branch by the body, no prompt, no cursor. */
  private checkExits(): void {
    for (const exit of this.def.exits) {
      if ((exit.when ?? []).some((f) => !hasFlag(f))) continue;
      const r = exit.radius ?? EXIT_RADIUS;
      const dx = this.player.x - exit.x;
      const dz = this.player.z - exit.z;
      if (dx * dx + dz * dz <= r * r) {
        this.takeExit(exit);
        return;
      }
    }
  }

  override update(time: number, delta: number): void {
    if (this.leaving) return;
    // Clamp dt so an alt-tab pause can't fling the deterministic sim.
    const dtSec = Math.min(delta / 1000, 1 / 30);

    // A watched beat holds the frame: the insert runs, the simulation does not.
    // (The cosmetic clocks below the early return keep breathing — a cutaway
    // does not stop the water — but control, actors, combat and exits pause.)
    if (this.edit?.watching) {
      this.edit.updateWatched(dtSec);
      const tw = time / 1000;
      this.skin.update(tw);
      for (const p of this.puddles) p.update(tw);
      return;
    }

    this.control.update();

    const sun = this.sun;
    for (const a of this.actors) {
      // Tell the rig how wet the ground under it is BEFORE it advances: the Rig
      // mirrors the same frame it draws, so the reflection can never lag the pose.
      const r = reflectionAt(this.reflective, a.x, a.z, a.h);
      a.reflect(r.strength, r.planeY);
      // How deep in the plate's shadow the actor stands: darkens him, and drops
      // his cast shadow to nothing (no direct sun reaches in to cast one).
      const shade = shadeAt(this.shade, a.x, a.z);
      a.setShade(shade);
      if (sun) a.castShadow(sun.dx, sun.dy, sun.strength * (1 - shade), sun.softness);
      a.update(dtSec);
    }

    // The cast obeys the same light the actors do — the chiaroscuro includes
    // everyone in the shot — and performs its attention against the player.
    for (const n of this.npcs) {
      const r = reflectionAt(this.reflective, n.x, n.z, 0);
      n.reflect(r.strength, r.planeY);
      const shade = shadeAt(this.shade, n.x, n.z);
      n.setShade(shade);
      if (sun) n.castShadow(sun.dx, sun.dy, sun.strength * (1 - shade), sun.softness);
      n.update(dtSec, this.player.x, this.player.z);
    }

    resolveCombat(
      this.actors.map((a) => a.hitbox),
      this.actors.map((a) => a.hurtbox),
    );

    this.edit?.updatePlayed(this.player.x, this.player.z);

    this.checkExits();
    if (this.leaving) return;

    if (!this.fixedCamera) {
      const cam = this.cameras.main;
      cam.scrollX = Phaser.Math.Linear(cam.scrollX, this.cameraTargetX(), CAMERA_LERP);
      cam.scrollY = 0;
    }

    // Cosmetic, and kept off the deterministic sim's dtSec.
    const t = time / 1000;
    this.skin.update(t);
    for (const p of this.puddles) p.update(t);

    this.hud.update(this.player.stateName);
  }
}
